import {
  CollapsibleDiv,
  Dropdown,
  DropdownMenu,
  DropdownRef,
  Input,
  Pill,
  Typography,
} from '@recruit-robin/robin-theme/web-components';
import { ChevronDown } from '@recruit-robin/robin-theme/web-icons';
import { get } from 'lodash';
import { useContext, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { employmentTypeOptions } from '../../../../utils/EnrichVacancyModal.constants';
import { JobFormValues } from '../../EditEnrichedVacancy.types';
import { CustomSelectForm } from './CustomSelectForm';
import { useStyles } from './FormSections.styles';
import { RenderField } from './RenderField';
import { useQualityStatus } from './useQualityStatus';

export const JobDetails = () => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);
  const { control, errors } = useFormContext<JobFormValues>();
  const salaryUnitDropdownRef = useRef<DropdownRef>(null);

  const { statusConfig } = useQualityStatus<JobFormValues>('job_details', {
    job_title: { required: true, weight: 35 },
    application_deadline: { weight: 0 },
    employment_type: { weight: 20 },
    min_salary: { required: true, weight: 15 },
    max_salary: { required: true, weight: 15 },
    salary_unit: { weight: 10 },
  });

  return (
    <CollapsibleDiv
      initialOpen
      limitToggleToTitle
      title={
        <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
          {dictionary.jobDetails}
        </Typography>
      }
      endAdornment={
        <Pill
          startIcon={statusConfig.icon}
          label={statusConfig.label}
          style="icon-removable"
          type={statusConfig.type}
          padding="text"
        />
      }
      customCss={classes.section}>
      <div css={classes.sectionContent}>
        <RenderField
          label={dictionary.jobTitle}
          fieldName="job_details.job_title"
          required
          render={(controllerArg) => (
            <Input
              {...controllerArg}
              type="text"
              placeholder={dictionary.jobTitlePlaceholder}
              contained
              hasError={get(errors, 'job_details.job_title')}
            />
          )}
        />

        <div css={classes.formRow}>
          <RenderField
            label={dictionary.employmentType}
            fieldName="job_details.employment_type"
            render={() => (
              <CustomSelectForm
                control={control}
                fieldName="job_details.employment_type"
                options={employmentTypeOptions}
                label={dictionary.employmentType}
                multiple
                limit
                selectAll
              />
            )}
          />

          <div css={classes.inputWithDropdown}>
            <div css={{ display: 'flex', gap: 8 }}>
              <RenderField
                required
                label={dictionary.minSalary || 'Min salary'}
                fieldName="job_details.min_salary"
                render={(controllerArg) => (
                  <Input
                    {...controllerArg}
                    type="text"
                    inputMode="decimal"
                    pattern="^\\d*([.,]\\d*)?$"
                    placeholder={dictionary.salaryRangePlaceholder}
                    contained
                    hasError={get(errors, 'job_details.min_salary')}
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value.replace(/[^\d.,]/g, '');
                      const dotIndex = value.indexOf('.');
                      const commaIndex = value.indexOf(',');
                      if (dotIndex !== -1 && commaIndex !== -1) {
                        if (dotIndex < commaIndex) {
                          value = value.slice(0, commaIndex).replace(/\./g, '') + value.slice(commaIndex);
                        } else {
                          value = value.slice(0, dotIndex).replace(/,/g, '') + value.slice(dotIndex);
                        }
                      } else if (dotIndex !== -1) {
                        value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
                      } else if (commaIndex !== -1) {
                        value = value.slice(0, commaIndex + 1) + value.slice(commaIndex + 1).replace(/,/g, '');
                      }
                      controllerArg.onChange(value);
                    }}
                  />
                )}
              />
              <RenderField
                required
                label={dictionary.maxSalary || 'Max salary'}
                fieldName="job_details.max_salary"
                render={(controllerArg) => (
                  <Input
                    {...controllerArg}
                    type="text"
                    inputMode="decimal"
                    pattern="^\\d*([.,]\\d*)?$"
                    placeholder={dictionary.salaryRangePlaceholderMax}
                    contained
                    hasError={get(errors, 'job_details.max_salary')}
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value.replace(/[^\d.,]/g, '');
                      const dotIndex = value.indexOf('.');
                      const commaIndex = value.indexOf(',');
                      if (dotIndex !== -1 && commaIndex !== -1) {
                        if (dotIndex < commaIndex) {
                          value = value.slice(0, commaIndex).replace(/\./g, '') + value.slice(commaIndex);
                        } else {
                          value = value.slice(0, dotIndex).replace(/,/g, '') + value.slice(dotIndex);
                        }
                      } else if (dotIndex !== -1) {
                        value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
                      } else if (commaIndex !== -1) {
                        value = value.slice(0, commaIndex + 1) + value.slice(commaIndex + 1).replace(/,/g, '');
                      }
                      controllerArg.onChange(value);
                    }}
                  />
                )}
              />
            </div>
            <RenderField
              fieldName="job_details.salary_unit"
              render={(controllerArg) => (
                <Dropdown
                  ref={salaryUnitDropdownRef}
                  placement="bottom-end"
                  zIndex={1400}
                  renderMenu={({ styles }) => (
                    <DropdownMenu
                      customCss={styles}
                      width={150}
                      onSelectOption={(option) => {
                        controllerArg.onChange(option.value);
                        salaryUnitDropdownRef.current?.close();
                      }}
                      options={[
                        { label: dictionary.hourly, value: 'hourly' },
                        { label: dictionary.weekly, value: 'weekly' },
                        { label: dictionary.monthly, value: 'monthly' },
                        { label: dictionary.yearly, value: 'yearly' },
                      ]}
                    />
                  )}>
                  {() => (
                    <div css={classes.dropdownButton}>
                      <ChevronDown size={16} />
                      <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
                        {dictionary[controllerArg.value?.toLowerCase() as 'hourly' | 'weekly' | 'monthly' | 'yearly'] ||
                          (dictionary.salaryUnit as string)}
                      </Typography>
                    </div>
                  )}
                </Dropdown>
              )}
            />
          </div>
        </div>
      </div>
    </CollapsibleDiv>
  );
};
